import type { ReactNode } from 'react'
import { installViewTransitions } from './lib/viewTransition'
import {
  createRootRoute,
  createRoute,
  createRouter,
  redirect,
  type RouteComponent,
} from '@tanstack/react-router'
import { Shell } from './components/Shell'
import { TodayView } from './views/Today'
import { HomeView } from './views/Home'
import { InboxView } from './views/Inbox'
import { UpcomingView } from './views/Upcoming'
import { ProjectsView } from './views/Projects'
import { ProjectView } from './views/Project'
import { PagesView } from './views/Pages'
import { CalendarView } from './views/Calendar'
import { PageView } from './views/Page'
import { SomedayView } from './views/Someday'
import { RecurringView } from './views/Recurring'
import { ReviewView } from './views/Review'
import { PlanView } from './views/Plan'
import { LogView } from './views/Log'
import { ActivityView } from './views/Activity'
import { SearchView } from './views/Search'
import { AllView } from './views/All'
import { ThemeEditView, ThemeGalleryView, ThemesView } from './views/Themes'
import { SettingsView } from './views/Settings'
import { TaskView } from './views/Task'
import { CaptureView } from './views/Capture'
import { CapturesView } from './views/Captures'
import { TrashView } from './views/Trash'
import { LibraryView } from './views/Library'
import { InsightsView } from './views/Insights'
import {
  MusicAlbumsView,
  MusicArtistsView,
  MusicPlaylistsView,
  MusicTracksView,
  MusicView,
} from './views/Music'
import { FocusView } from './views/Focus'
import { SpotifyCallbackView } from './views/SpotifyCallback'
import { MorningView } from './views/Morning'
import { RecapView } from './views/Recap'
import { WellbeingView } from './views/Wellbeing'
import { DatesView } from './views/Dates'
import { PeopleView } from './views/People'
import { DayView } from './views/Day'
import { ResetView } from './views/Reset'
import { CheckinView } from './views/Checkin'
import { ListsView } from './views/Lists'
import { ListView } from './views/List'
import { ReleasesView } from './views/dev/Releases'
import { InfraView } from './views/Infra'
import { InfraHostsView } from './views/InfraHosts'
import { InfraStorageView } from './views/InfraStorage'
import { DesignView } from './views/dev/Design'
import { AgentGraphView } from './views/AgentGraph'
import { MODES, type GraphMode } from './lib/agentGraph'
import { PRReviewView } from './views/dev/Review'
import { AgentRunView } from './views/AgentRun'
import { AgentStatsView } from './views/AgentStats'
import { PipelineView } from './views/Pipeline'
import { AgentsHome } from './views/AgentsHome'
import { MemberPrimeView } from './views/MemberPrime'
import { PRIME_APP } from './lib/appMode'
import { ChatView } from './views/Chat'
import { LeaveView } from './views/Leave'
import { WhatsNewView } from './views/WhatsNewPage'
import { LearnHomeView } from './views/learn/LearnHome'
import { CourseView } from './views/learn/CourseView'
import { LearnLibraryView } from './views/learn/LibraryView'
import { ConceptView } from './views/learn/ConceptView'
import { DeckView } from './views/learn/DeckView'
import { LearnTodayView } from './views/learn/TodayView'
import { ProgressView } from './views/learn/ProgressView'
import { LearnSettingsView } from './views/learn/LearnSettingsView'
import { NotFoundView } from './views/NotFound'
import '@/packs/dev'
import '@/packs/learn'
import '@/packs/infra'
import '@/packs/files'
import { FileRouteView, FilesView } from './views/files/FilesView'

const root = createRootRoute({ component: Shell })
const r = (path: string, component: () => ReactNode) =>
  createRoute({ getParentRoute: () => root, path, component: component as RouteComponent })
/** A page that went: its path redirects to the one that took its place. */
const gone = (path: string, to: '/agents/graph' | '/agents/all', search?: { mode: GraphMode }) =>
  createRoute({
    getParentRoute: () => root,
    path,
    beforeLoad: () => {
      throw redirect({ to, search: search as never })
    },
  })

const routeTree = root.addChildren([
  // the Prime app opens on Prime (docs/prime-app.md); alfieOS on Today
  createRoute({
    getParentRoute: () => root,
    path: '/',
    component: TodayView as RouteComponent,
    beforeLoad: () => {
      if (PRIME_APP) throw redirect({ to: '/agents' })
    },
  }),
  r('/home', HomeView),
  r('/inbox', InboxView),
  r('/upcoming', UpcomingView),
  r('/projects', ProjectsView),
  r('/projects/$id', ProjectView),
  r('/calendar', CalendarView),
  r('/pages', PagesView),
  createRoute({
    getParentRoute: () => root,
    path: '/all',
    component: AllView as RouteComponent,
    validateSearch: (s: Record<string, unknown>) => ({
      tab: typeof s.tab === 'string' ? s.tab : undefined,
      q: typeof s.q === 'string' ? s.q : undefined,
    }),
  }),
  r('/pages/$id', PageView),
  r('/someday', SomedayView),
  r('/recurring', RecurringView),
  r('/review', ReviewView),
  r('/log', LogView),
  r('/activity', ActivityView),
  r('/lists', ListsView),
  r('/lists/$id', ListView),
  r('/captures', CapturesView),
  r('/trash', TrashView),
  r('/library', LibraryView),
  createRoute({
    getParentRoute: () => root,
    path: '/music/insights',
    component: InsightsView as RouteComponent,
    validateSearch: (s: Record<string, unknown>) => {
      const str = (k: string) => (typeof s[k] === 'string' && s[k] ? (s[k] as string) : undefined)
      const date = (k: string) =>
        typeof s[k] === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(s[k] as string) ? (s[k] as string) : undefined
      return {
        metric: str('metric'),
        days: [7, 30, 90, 365, 0].includes(Number(s.days)) ? Number(s.days) : undefined,
        since: date('since'),
        until: date('until'),
        artist: str('artist'),
        album: str('album'),
        item: str('item'),
      }
    },
  }),
  r('/music', MusicView),
  r('/music/artists', MusicArtistsView),
  r('/music/albums', MusicAlbumsView),
  r('/music/tracks', MusicTracksView),
  r('/music/playlists', MusicPlaylistsView),
  createRoute({
    getParentRoute: () => root,
    path: '/library/insights',
    beforeLoad: ({ search }) => {
      throw redirect({ to: '/music/insights', search: search as never })
    },
  }),
  // the desk folded into Home (2026-10-10): its weather, house, lyrics and queue are widgets there
  createRoute({
    getParentRoute: () => root,
    path: '/desk',
    beforeLoad: () => {
      throw redirect({ to: '/home' })
    },
  }),
  r('/focus', FocusView),
  r('/spotify/callback', SpotifyCallbackView),
  r('/leave', LeaveView),
  r('/whats-new', WhatsNewView),
  r('/learn', LearnHomeView),
  r('/learn/course/$id', CourseView),
  r('/learn/deck', DeckView),
  r('/learn/today', LearnTodayView),
  r('/learn/progress', ProgressView),
  r('/learn/settings', LearnSettingsView),
  r('/learn/library', LearnLibraryView),
  r('/learn/concept/$id', ConceptView),
  r('/captures/$id', CaptureView),
  r('/morning', MorningView),
  r('/recap', RecapView),
  createRoute({
    getParentRoute: () => root,
    path: '/plan',
    component: PlanView as RouteComponent,
    validateSearch: (s: Record<string, unknown>): { for?: 'week' | 'weekend' } =>
      s.for === 'week' || s.for === 'weekend' ? { for: s.for } : {},
  }),
  r('/wellbeing', WellbeingView),
  r('/dates', DatesView),
  r('/dates/$id', DatesView),
  r('/day', DayView),
  r('/people', PeopleView),
  r('/people/$id', PeopleView),
  r('/reset', ResetView),
  r('/checkin', CheckinView),
  createRoute({
    getParentRoute: () => root,
    path: '/search',
    component: SearchView as RouteComponent,
    validateSearch: (s: Record<string, unknown>) => ({ q: typeof s.q === 'string' ? s.q : undefined }),
  }),
  r('/themes', ThemesView),
  r('/themes/gallery', ThemeGalleryView),
  createRoute({
    getParentRoute: () => root,
    path: '/themes/new',
    component: ThemeEditView as RouteComponent,
    validateSearch: (s: Record<string, unknown>) => ({
      from: typeof s.from === 'string' ? s.from : undefined,
    }),
  }),
  r('/themes/$id', ThemeEditView),
  r('/settings', SettingsView),
  r('/settings/$section', SettingsView),
  r('/tasks/$id', TaskView),
  r('/dev/releases', ReleasesView),
  // the infra pack (2026-10-31): every estate at a glance over alerts, the security posture, the
  // cluster, storage and the security programme (2026-10-23); and every machine
  r('/infra', InfraView),
  r('/infra/hosts', InfraHostsView),
  r('/infra/storage', InfraStorageView),
  // the files pack (docs/areas/files.md): the browser and Recent, Starred, Deleted as ?tab=; a folder or file by id
  createRoute({
    getParentRoute: () => root,
    path: '/files',
    component: FilesView as RouteComponent,
    validateSearch: (s: Record<string, unknown>) => ({
      tab: ['recent', 'starred', 'deleted'].includes(s.tab as string) ? (s.tab as string) : undefined,
      q: typeof s.q === 'string' ? s.q : undefined,
    }),
  }),
  r('/files/$id', FileRouteView),
  r('/dev/design', DesignView),
  r('/dev/review/$id', PRReviewView),
  // DevOS's list pages the Graph took over (2026-10-10, "Graph is the hub"): a
  // bookmark or an old push lands on what replaced it
  gone('/dev/board', '/agents/graph', { mode: 'work' }),
  gone('/dev/infra', '/agents/graph', { mode: 'infra' }),
  gone('/dev/reviews', '/agents/graph'),
  gone('/dev/issues', '/agents/graph'),
  gone('/dev/overview', '/agents/graph'),
  gone('/dev/runs', '/agents/all'),
  createRoute({
    getParentRoute: () => root,
    path: '/dev/runs/$id',
    beforeLoad: ({ params }) => {
      throw redirect({ to: '/agents/run/$id', params })
    },
  }),
  r('/agents', ChatView),
  r('/agents/$id', ChatView),
  r('/agents/run/$id', AgentRunView),
  r('/agents/stats', AgentStatsView),
  r('/agents/pipeline', PipelineView),
  createRoute({
    getParentRoute: () => root,
    path: '/agents/graph',
    component: AgentGraphView as RouteComponent,
    // ?mode= opens the graph in that view (the old Board and Infrastructure land here)
    validateSearch: (s: Record<string, unknown>): { mode?: GraphMode } =>
      MODES.some((m) => m.key === s.mode) ? { mode: s.mode as GraphMode } : {},
  }),
  r('/agents/all', AgentsHome),
  r('/ask', MemberPrimeView),
  // the Prime app's Overview went with the Graph's arrival as the hub
  gone('/prime/overview', '/agents/graph'),
  createRoute({
    getParentRoute: () => root,
    path: '/prime',
    beforeLoad: () => {
      throw redirect({ to: '/agents' })
    },
  }),
  createRoute({
    getParentRoute: () => root,
    path: '/today',
    beforeLoad: () => {
      throw redirect({ to: '/' })
    },
  }),
])

export const router = createRouter({
  routeTree,
  defaultPreload: false,
  scrollRestoration: true,
  // a path that is not a page: drawn inside the Shell, with a way back
  defaultNotFoundComponent: NotFoundView,
})

installViewTransitions(router)

declare module '@tanstack/react-router' {
  interface Register {
    router: typeof router
  }
}
